import React, { useId, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { useT } from "../../lib/LanguageContext";
import { uploadProblem } from "./usePlatformMedia";
import { formatMegabytes, siteErrorMessage } from "./siteErrors";

/** "image/png" → "PNG": the accepted types as an operator reads them. */
const typeLabels = (types) => types.map((mt) => mt.split("/")[1].toUpperCase()).join(", ");

/**
 * One-file image upload into the platform media library, with the schema's type and size
 * rules checked before the request (the server stays the authority: it decides the type
 * by magic bytes). Used by the Media tab and by the media picker.
 *
 * @param {object} props
 * @param {object} props.schema     `GET /schema` body (`media_types`, `limits.media_bytes`)
 * @param {(file: File) => Promise<object>} props.upload  `usePlatformMedia().upload`
 * @param {(media: object) => void} [props.onUploaded]
 * @param {string} props.testId     `data-testid` of the file input; the error line is
 *                                  `${testId}-error`
 */
export default function MediaUploadButton({ schema, upload, onUploaded, testId }) {
  const t = useT("superadmin");
  const inputId = useId();
  const hintId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFile = async (file) => {
    setError("");
    const problem = uploadProblem(file, schema);
    if (problem === "type") {
      setError(t("hsite.media.wrongType", { types: typeLabels(schema.media_types) }));
      return;
    }
    if (problem === "size") {
      setError(t("hsite.media.tooBig", { max: formatMegabytes(schema.limits.media_bytes) }));
      return;
    }
    setBusy(true);
    try {
      const media = await upload(file);
      toast.success(t("hsite.media.uploaded"));
      if (onUploaded) onUploaded(media);
    } catch (e) {
      setError(siteErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label
        htmlFor={inputId}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-sol-gold text-black hover:bg-sol-gold/90 cursor-pointer focus-within:ring-2 focus-within:ring-sol-gold/50 ${busy ? "opacity-60 pointer-events-none" : ""}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {busy ? t("hsite.media.uploading") : t("hsite.media.upload")}
        <input
          id={inputId}
          type="file"
          accept={schema.media_types.join(",")}
          disabled={busy}
          aria-describedby={hintId}
          className="sr-only"
          data-testid={testId}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Cleared so choosing the same file again (after fixing it) fires a change.
            e.target.value = "";
            if (file) onFile(file);
          }}
        />
      </label>
      <p id={hintId} className="mt-1 text-xs text-slate-400">
        {t("hsite.media.rules", {
          types: typeLabels(schema.media_types),
          max: formatMegabytes(schema.limits.media_bytes),
        })}
      </p>
      {error && (
        <p role="alert" className="mt-1 text-xs text-rose-600" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
